import { DateTime } from 'luxon';

export function generateCallSchedule(startDate, endDate, residents, schedulingPreference, schedulerMode, handoverDay = 1) {
  startDate = DateTime.fromJSDate(startDate).startOf('day');
  endDate = DateTime.fromJSDate(endDate).endOf('day');

  const totalDays = endDate.diff(startDate, 'days').days + 1;
  const dates = Array.from({ length: totalDays }, (_, i) => startDate.plus({ days: i }));
  const schedule = dates.reduce((acc, date) => ({ ...acc, [date.toISODate()]: null }), {});

  // Randomize the order of residents
  const shuffledResidents = [...residents].sort(() => Math.random() - 0.5);

  const lastCall = shuffledResidents.reduce((acc, resident) => ({ ...acc, [resident.name]: null }), {});
  const totalShifts = shuffledResidents.reduce((acc, resident) => ({ ...acc, [resident.name]: 0 }), {});
  const weekendDays = ['5', '6', '7']; // Fri, Sat, Sun

  const canTakeCall = (resident, date, duration = 1) => {
    for (let i = 0; i < duration; i++) {
      const currentDate = date.plus({ days: i });
      if (currentDate < startDate || currentDate > endDate) return false;
      const dateStr = currentDate.toISODate();
      if (resident.daysOff && resident.daysOff.includes(dateStr)) return false;
      if (schedule[dateStr]) return false;
    }
    const lastCallDate = lastCall[resident.name];
    if (!lastCallDate) return true;
    const daysSinceLastCall = date.diff(DateTime.fromJSDate(lastCallDate), 'days').days;
    if (schedulerMode === 'resident' && resident.max1inX) {
      return daysSinceLastCall >= resident.max1inX;
    }
    return daysSinceLastCall >= 1;
  };

  const getResidentScore = (resident, date, preference) => {
    const lastCallDate = lastCall[resident.name];
    const daysSinceLastCall = lastCallDate 
      ? date.diff(DateTime.fromJSDate(lastCallDate), 'days').days 
      : Infinity;
    const shiftDeficit = resident.max1inX
      ? (totalDays / resident.max1inX) - totalShifts[resident.name]
      : -totalShifts[resident.name];

    let score = daysSinceLastCall + shiftDeficit * 10;
    if (preference === 'minimize_weekend' && weekendDays.includes(date.weekday.toString())) {
      score -= 100; // Lower score to prioritize minimizing weekend shifts
    }
    return score;
  };

  const assignShift = (resident, date) => {
    schedule[date.toISODate()] = resident.name;
    lastCall[resident.name] = date.toJSDate();
    totalShifts[resident.name]++;
  };

  const fillResidentShifts = (preference) => {
    dates.forEach(date => {
      const dateStr = date.toISODate();
      if (!schedule[dateStr]) {
        const availableResidents = shuffledResidents.filter(resident => 
          canTakeCall(resident, date, 1)
        );
        
        if (availableResidents.length > 0) {
          const selectedResident = availableResidents.reduce((a, b) => 
            getResidentScore(b, date, preference) > getResidentScore(a, date, preference) ? b : a
          );
          assignShift(selectedResident, date);
        }
      }
    });

    if (preference === 'minimize_weekend') {
      optimizeWeekendShifts();
    }
  };

  const fillAttendingShifts = () => {
    let firstFullWeekStart = startDate.set({ weekday: handoverDay });
    if (firstFullWeekStart < startDate) firstFullWeekStart = firstFullWeekStart.plus({ weeks: 1 });
    let lastFullWeekEnd = endDate.set({ weekday: handoverDay - 1 });
    if (lastFullWeekEnd > endDate) lastFullWeekEnd = lastFullWeekEnd.minus({ weeks: 1 });

    // Distribute full weeks
    let currentDate = firstFullWeekStart;
    let residentIndex = 0;
    while (currentDate <= lastFullWeekEnd) {
      const weekEndDate = currentDate.plus({ days: 6 });
      let assignedResident = null;
      
      for (let i = 0; i < shuffledResidents.length; i++) {
        const resident = shuffledResidents[(residentIndex + i) % shuffledResidents.length];
        if (canTakeCall(resident, currentDate, 7)) {
          assignedResident = resident;
          break;
        }
      }

      if (assignedResident) {
        assignWeekShift(assignedResident, currentDate, weekEndDate);
        residentIndex = (shuffledResidents.indexOf(assignedResident) + 1) % shuffledResidents.length;
      }

      currentDate = currentDate.plus({ weeks: 1 });
    }

    // Handle partial week at the start
    if (startDate < firstFullWeekStart) {
      const availableResidents = shuffledResidents.filter(resident => 
        canTakeCall(resident, startDate, firstFullWeekStart.diff(startDate, 'days').days)
      );
      if (availableResidents.length > 0) {
        const selectedResident = availableResidents.reduce((a, b) => 
          totalShifts[a.name] < totalShifts[b.name] ? a : b
        );
        assignWeekShift(selectedResident, startDate, firstFullWeekStart.minus({ days: 1 }));
      }
    }

    // Handle partial week at the end
    if (endDate > lastFullWeekEnd) {
      const availableResidents = shuffledResidents.filter(resident => 
        canTakeCall(resident, lastFullWeekEnd.plus({ days: 1 }), endDate.diff(lastFullWeekEnd, 'days').days)
      );
      if (availableResidents.length > 0) {
        const selectedResident = availableResidents.reduce((a, b) => 
          totalShifts[a.name] < totalShifts[b.name] ? a : b
        );
        assignWeekShift(selectedResident, lastFullWeekEnd.plus({ days: 1 }), endDate);
      }
    }
  };

  const assignWeekShift = (resident, startOfWeek, endOfWeek) => {
    let currentDate = startOfWeek;
    while (currentDate <= endOfWeek) {
      schedule[currentDate.toISODate()] = resident.name;
      totalShifts[resident.name]++;
      currentDate = currentDate.plus({ days: 1 });
    }
  };

  const optimizeWeekendShifts = () => {
    const weekendDates = dates.filter(date => weekendDays.includes(date.weekday.toString()));
    
    for (let i = 0; i < weekendDates.length; i++) {
      for (let j = i + 1; j < weekendDates.length; j++) {
        const dateA = weekendDates[i];
        const dateB = weekendDates[j];
        const residentA = schedule[dateA.toISODate()];
        const residentB = schedule[dateB.toISODate()];

        if (residentA && residentB && residentA !== residentB) {
          const scoreA = getResidentScore({ name: residentA }, dateA, 'minimize_weekend');
          const scoreB = getResidentScore({ name: residentB }, dateB, 'minimize_weekend');
          const swappedScoreA = getResidentScore({ name: residentA }, dateB, 'minimize_weekend');
          const swappedScoreB = getResidentScore({ name: residentB }, dateA, 'minimize_weekend');

          if (swappedScoreA + swappedScoreB > scoreA + scoreB) {
            // Swap the shifts
            schedule[dateA.toISODate()] = residentB;
            schedule[dateB.toISODate()] = residentA;
          }
        }
      }
    }
  };

  if (schedulerMode === 'resident') {
    fillResidentShifts(schedulingPreference);
  } else if (schedulerMode === 'attending') {
    fillAttendingShifts();
  }

  // Optimize days off according to resident preferences
  residents.forEach(resident => {
    if (resident.daysOff) {
      resident.daysOff.forEach(day => {
        const date = DateTime.fromISO(day);
        if (canTakeCall(resident, date)) {
          assignShift(resident, date);
        }
      });
    }
  });

  // Generate warnings
  const warnings = [];
  let hasEmptySpots = false;
  let hasBackToBack = false;
  let prevResident = null;
  let emptySpots = 0;
  let backToBack = 0;
  
  Object.entries(schedule).forEach(([date, resident]) => {
    if (resident === null) {
      hasEmptySpots = true;
      emptySpots++;
    } else if (schedulerMode === 'resident' && resident === prevResident) {
      hasBackToBack = true;
      backToBack++;
    }
    prevResident = resident;
  });

  if (hasEmptySpots) {
    warnings.push(`The schedule contains ${emptySpots} empty spot(s).`);
  }
  if (hasBackToBack && schedulerMode === 'resident') {
    warnings.push(`The schedule contains ${backToBack} back-to-back shift(s).`);
  }

  return { schedule, totalShifts, warnings };
}